"use client";
import Container from "@/components/container";
import { Col, Row, Avatar, Flex, Modal, Form, App } from "antd";
import placeholder from "@/images/placeholder.png";
import styles from "../Event/event.module.css";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

import { IconCalendarEvent, IconGlobe, IconMapPin } from "@/lib/icons";
import {
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { formatEventDate, formatEventTime } from "@/lib/formatDateTime";
import { Ticket } from "./ticketStep";
import {
  SeasonPassCoveredEventsModal,
  readableTicketDescription,
} from "./SeasonPassTicketCard";
import CreateCustomForm from "./CreateCustomForm";
import { ENDPOINTS } from "@/constants/endpoint";
import { DEV_AUTH_HEADERS } from "@/lib/devHeaders";
import { usercontext } from "@/lib/context";
import { useRouter, useSearchParams } from "next/navigation";
import {
  trackPurchase as trackPixelPurchase,
  trackAddPaymentInfo,
} from "@/lib/metaPixel";
import LocationButton from "../LocationButton";
import EventTimezone from "../EventTimezone";
import Link from "next/link";
import { useLayout } from "@/lib/LayoutContext";
import ComponentToRender from "./ComponentToRender";
import { deriveMultiDateCondition, getInitialStep } from "./stepFlow";
import {
  findDuplicatePhones,
  REGISTRATION_MESSAGES,
} from "@/lib/registrationHelpers";
import { countryCodeToPhoneCode } from "@/components/CustomPhoneInput";
import { useEventEnrollmentCheck } from "@/lib/useEventEnrollmentCheck";
import { eventUrl } from "@/lib/routes";
import {
  getReferralCode,
  storeReferralCode,
  clearReferralCode,
} from "@/lib/referralCode";
import {
  trackAddAttendeeInfo,
  trackPaymentStarted,
  trackPurchase,
  trackPaymentFailed,
  trackBookingPageView,
} from "@/lib/analytics";
import ConvertedTime from "../convertedtime";
import { resolveVenueLabel } from "../EventSummary";
import { logger } from "@/lib/logger";

const Booking = ({ data: rawData, customize, status: statusProp }) => {
  const { message } = App.useApp();
  const data = rawData?.parent_event
    ? { ...rawData.parent_event, grouped_data: rawData.grouped_data }
    : rawData;

  const {
    name,
    cover_image,
    tickets,
    show_single_registration,
    is_custom_form,
    layout_type,
    ticket_custom_field,
    address,
    venue,
    yet_to_be_announced,
    start_date,
    start_time,
    timezone,
    slug,
    feature_images,
    id,
    end_date,
    end_time,
    season_tickets,
    event_settings,
    event_meta,
    sold_out: sld,
    is_zone_config,
    is_contest,
    is_ticket_transferable,
    is_unique_registration,
    add_gst,
    add_convenience_fees,
    show_available_tickets,
    currency,
    status: _status,
    description,
    category_details,
    about,
    facilities,
    organizer_details,
    terms_and_conditions,
    artists_details,
    social_media,
    video_url,
    genre_details,
    ...restData
  } = data;

  const isFinished = (statusProp ?? data?.status) === "completed";

  const { setOffset, removeOffset } = useLayout();
  const footerRef = useRef(null);

  logger.info("Booking props:", data);
  useEffect(() => {
    const updateOffset = () => {
      if (footerRef.current && window.innerWidth <= 575) {
        setOffset("booking_footer", footerRef.current.offsetHeight);
      } else {
        removeOffset("booking_footer");
      }
    };

    updateOffset();
    window.addEventListener("resize", updateOffset);

    let resizeObserver;
    if (footerRef.current && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateOffset);
      resizeObserver.observe(footerRef.current);
    }

    return () => {
      window.removeEventListener("resize", updateOffset);
      resizeObserver?.disconnect();
      removeOffset("booking_footer");
    };
  }, [setOffset, removeOffset]);

  useEffect(() => {
    if (id || slug) {
      trackBookingPageView({
        event_id: id,
        event_name: name,
        event_city: address?.city || venue?.city,
      });
    }
  }, [id, slug, name, address?.city, venue?.city]);

  const restEntries = restData ? Object.entries(restData) : [];

  const cityEntries = restEntries.filter(
    ([, v]) => v !== null && typeof v === "object" && !Array.isArray(v),
  );

  const [additionalData] = cityEntries;
  const [, additionalDataObj] = additionalData || [];
  const { sold_out, ...restVenue } = additionalDataObj || {};
  const total_venue = cityEntries.length;

  const hasRegularTickets = Array.isArray(tickets) && tickets.length > 0;
  const hasGroupedData = cityEntries.length > 0;
  const isGroup = hasGroupedData && !hasRegularTickets;

  const total_sub_venue = Object.entries(restVenue || {}).length;
  const firstSubVenueEntry = Object.entries(restVenue || {})[0] || [];
  const { venue_name, address: adres, dates } = firstSubVenueEntry[1] || {};

  const total_event_date = Object.entries(dates || {}).length;
  const firstDateEntry = Object.entries(dates || {})[0] || [];
  const selectedDate = firstDateEntry[0];
  const firstDateData = firstDateEntry[1] || {};
  const events = Array.isArray(firstDateData)
    ? firstDateData
    : firstDateData.events;

  const total_event_time = (events || []).length;

  const [orderDetails, setOrderDetails] = useState(() => {
    if (total_venue === 1 && total_sub_venue === 1) {
      return {
        parent_event_id: id,
        venue: venue_name,
        timezone: events?.[0]?.timezone || timezone,
        address: adres,
        date:
          total_event_date === 1 && total_event_time === 1
            ? selectedDate
            : null,
        time:
          total_event_date === 1 && total_event_time === 1
            ? events?.[0]?.start_time
            : null,
        end_date:
          total_event_date === 1 && total_event_time === 1
            ? events?.[0]?.end_date
            : null,
        end_time:
          total_event_date === 1 && total_event_time === 1
            ? events?.[0]?.end_time
            : null,
        event_id:
          total_event_date === 1 && total_event_time === 1
            ? events?.[0]?.child_event_id
            : null,
        layout_type:
          total_event_date === 1 && total_event_time === 1
            ? events?.[0]?.layout_type
            : null,
        seating_configuration:
          total_event_date === 1 && total_event_time === 1
            ? events?.[0]?.seating_configuration
            : null,
      };
    }
    return null;
  });

  const haveConfiguredSeats =
    tickets !== undefined
      ? Boolean(data?.seating_configuration)
      : Boolean(
          data?.seating_configuration || orderDetails?.seating_configuration,
        );

  const [zoneEnabled, setZoneEnabled] = useState(() =>
    Boolean(data?.is_zone_config),
  );
  const [seatingAvailable, setSeatingAvailable] = useState(null);

  const haveZoneLayout = isGroup ? orderDetails?.is_zone_config : zoneEnabled;

  const current_seating_config = isGroup
    ? (orderDetails?.seating_configuration ?? data?.seating_configuration)
    : data?.seating_configuration;

  const effectiveHaveConfiguredSeats =
    seatingAvailable === false ? false : haveConfiguredSeats;

  const is_zone = haveZoneLayout;

  const is_sitting = effectiveHaveConfiguredSeats
    ? current_seating_config
    : false;

  const needsVenueSelection =
    isGroup && !(total_venue === 1 && total_sub_venue === 1);
  const knownDates = !needsVenueSelection ? dates : null;

  const [selectedDates, setSelectedDates] = useState(knownDates);
  const [selectedTickets, setSelectedTickets] = useState(() =>
    total_event_date === 1 && total_event_time === 1
      ? events?.[0]?.tickets
      : null,
  );
  const selectedTicketMeta = useMemo(() => {
    const selectedId = orderDetails?.ticket?.id;
    if (!selectedId) return null;

    const pool =
      (Array.isArray(selectedTickets) && selectedTickets.length > 0
        ? selectedTickets
        : null) ||
      (Array.isArray(tickets) && tickets.length > 0 ? tickets : null) ||
      [];

    return pool.find((t) => String(t?.id) === String(selectedId)) || null;
  }, [orderDetails?.ticket?.id, selectedTickets, tickets]);

  const selectedTicketIndex = useMemo(() => {
    const selectedId = orderDetails?.ticket?.id;
    if (!selectedId) return 0;
    const pool =
      (Array.isArray(selectedTickets) && selectedTickets.length > 0
        ? selectedTickets
        : null) ||
      (Array.isArray(tickets) && tickets.length > 0 ? tickets : null) ||
      [];
    const idx = pool.findIndex((t) => String(t?.id) === String(selectedId));
    return idx !== -1 ? idx : 0;
  }, [orderDetails?.ticket?.id, selectedTickets, tickets]);

  const [activeStep, setActiveStep] = useState(() => {
    if (hasRegularTickets) {
      return getInitialStep({
        isGroup: false,
        needsVenueSelection: false,
        isMultiDate: false,
        isZone: is_zone,
        isSitting: Boolean(is_sitting),
        haveConfiguredSeats: effectiveHaveConfiguredSeats,
      });
    }
    if (!needsVenueSelection) {
      const isMultiDate = deriveMultiDateCondition(dates);
      return getInitialStep({
        isGroup: true,
        needsVenueSelection: false,
        isMultiDate,
        isZone: is_zone,
        isSitting: Boolean(is_sitting),
        haveConfiguredSeats: effectiveHaveConfiguredSeats,
      });
    }
    return getInitialStep({
      isGroup: true,
      needsVenueSelection: true,
      isMultiDate: false,
      isZone: false,
      isSitting: false,
      haveConfiguredSeats: false,
    });
  });
  const [selectedSeats, setSelectedSeats] = useState([]);

  const [customModal, setCustomModal] = useState(false);
  const [approvalModal, setApprovalModal] = useState(false);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const enrollmentEventId = isGroup ? orderDetails?.event_id : id;
  const { enrolled: alreadyRegistered, order_id: enrollmentOrderId } =
    useEventEnrollmentCheck(enrollmentEventId, is_unique_registration);
  const { user } = useContext(usercontext);
  const router = useRouter();
  const searchParams = useSearchParams();
  const soldOutResetRef = useRef(null);
  const buyerInfoRef = useRef(null);

  useEffect(() => {
    const refFromUrl = searchParams.get("referral_code");
    if (refFromUrl) storeReferralCode(refFromUrl);
  }, [searchParams]);

  useEffect(() => {
    if (!orderDetails?.ticket?.id) {
      soldOutResetRef.current = null;
      return;
    }

    const ticketPool =
      Array.isArray(selectedTickets) && selectedTickets.length > 0
        ? selectedTickets
        : Array.isArray(tickets)
          ? tickets
          : [];

    const selected = ticketPool.find(
      (t) => String(t?.id) === String(orderDetails.ticket.id),
    );
    if (!selected) return;

    const available = Number(
      selected?.available ?? selected?.available_quantity ?? 0,
    );
    const minPerTxn = Number(selected?.min_per_transaction ?? 1);
    const soldOutFlag =
      selected?.is_sold_out === true ||
      selected?.is_sold_out === 1 ||
      selected?.is_sold_out === "1";
    const unavailable =
      soldOutFlag ||
      !Number.isFinite(available) ||
      available <= 0 ||
      available < minPerTxn;

    if (!unavailable) {
      soldOutResetRef.current = null;
      return;
    }

    if (soldOutResetRef.current !== String(orderDetails.ticket.id)) {
      soldOutResetRef.current = String(orderDetails.ticket.id);
      message.warning(
        "Selected ticket is sold out. Please choose another ticket.",
      );
    }

    setOrderDetails((prev) =>
      prev ? { ...prev, ticket: null, subtotal: 0, total_amount: 0 } : prev,
    );
    setActiveStep("tickets");
  }, [orderDetails?.ticket?.id, selectedTickets, tickets, setOrderDetails]);

  const handleNoZones = useCallback(() => {
    setZoneEnabled(false);
    setSeatingAvailable(false);
    setActiveStep("tickets");
  }, []);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };
  const is_required = orderDetails?.ticket?.requires_approval;

  const paymentMethod = async (data, buyerData) => {
    const buyer = buyerData || buyerInfoRef.current;

    const prefillName = (() => {
      const buyerName =
        [buyer?.first_name, buyer?.last_name]
          .filter(Boolean)
          .join(" ")
          .trim() ||
        (typeof buyer?.name === "string" ? buyer.name.trim() : "") ||
        (typeof buyer?.full_name === "string" ? buyer.full_name.trim() : "");
      if (buyerName) return buyerName;

      const userName =
        [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() ||
        (typeof user?.name === "string" ? user.name.trim() : "") ||
        (typeof user?.full_name === "string" ? user.full_name.trim() : "") ||
        [user?.profile?.first_name, user?.profile?.last_name]
          .filter(Boolean)
          .join(" ")
          .trim();
      if (userName) return userName;

      return undefined;
    })();

    const prefillContact = (() => {
      const rawPhone = buyer?.phone || user?.phone;
      const rawCountryCode = buyer?.country_code || user?.country_code;
      if (!rawPhone) return undefined;

      const phoneStr = String(rawPhone).trim();
      if (!phoneStr) return undefined;

      if (phoneStr.startsWith("+")) {
        const cleaned = "+" + phoneStr.replace(/\D/g, "");
        return cleaned.length > 3 ? cleaned : undefined;
      }

      if (rawCountryCode) {
        const dialCode =
          countryCodeToPhoneCode[rawCountryCode] || rawCountryCode;
        const cleanDial = String(dialCode).replace(/\D/g, "");
        const cleanDigits = phoneStr.replace(/\D/g, "");
        if (cleanDial && cleanDigits.startsWith(cleanDial)) {
          return `+${cleanDigits}`;
        }
        if (cleanDial && cleanDigits) {
          return `+${cleanDial}${cleanDigits}`;
        }
      }

      const digits = phoneStr.replace(/\D/g, "");
      if (!digits) return undefined;

      if (digits.length === 10) {
        return `+91${digits}`;
      }
      if (digits.length === 12 && digits.startsWith("91")) {
        return `+${digits}`;
      }
      return `+${digits}`;
    })();

    const prefillEmail = (() => {
      const email = buyer?.email || user?.email;
      return email ? String(email).trim() : undefined;
    })();

    trackPaymentStarted({
      order_id: data?.notes?.order_id || data?.id,
      event_id: orderDetails?.ticket?.event_id || id,
      event_name: name,
      value: orderDetails?.total_amount,
      currency: orderDetails?.ticket?.currency || "INR",
    });
    trackAddPaymentInfo({
      eventId: orderDetails?.ticket?.event_id || id,
      eventName: name,
      ticketId: orderDetails?.ticket?.id,
      ticketName: orderDetails?.ticket?.name,
      value: orderDetails?.total_amount,
      currency: orderDetails?.ticket?.currency || "INR",
    });

    try {
      const res = await loadRazorpayScript();
      if (!res) {
        message.error("Razorpay SDK failed to load. Are you online?");
        return;
      }

      const options = {
        key: data?.key,
        amount: data.amount,
        currency: data.currency,
        name: process.env.NEXT_PUBLIC_RZY_NAME,
        description: data?.receipt,
        image: "https://d1pr1hd5j9uqt.cloudfront.net/3314/logo.png",
        order_id: data?.id,

        prefill: {
          name: prefillName,
          email: prefillEmail,
          contact: prefillContact,
        },
        theme: {
          color: "#6AEAD7",
        },

        handler: async function (response) {
          setLoading(true);
          const res = await fetch(
            process.env.NEXT_PUBLIC_API_URL + "/" + ENDPOINTS.VERIFY_PAYMENT,
            {
              method: "POST",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
                ...DEV_AUTH_HEADERS,
              },
              body: JSON.stringify({
                order_id: data?.notes?.order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
              }),
            },
          );
          const rz = await res.json();
          if (rz.status) {
            trackPurchase({
              transaction_id:
                data?.notes?.order_id || response.razorpay_order_id,
              event_id: orderDetails?.ticket?.event_id || id,
              event_name: name,
              ticket_id: orderDetails?.ticket?.id,
              ticket_name: orderDetails?.ticket?.name,
              price: orderDetails?.ticket?.price,
              quantity: orderDetails?.ticket?.qty || 1,
              value: orderDetails?.total_amount,
              currency: orderDetails?.ticket?.currency || "INR",
              event_city: address?.city || (venue || {})?.city,
              event_date: orderDetails?.date,
              coupon: orderDetails?.discount,
            });
            trackPixelPurchase({
              orderId: data?.notes?.order_id || response.razorpay_order_id,
              eventId: orderDetails?.ticket?.event_id || id,
              eventName: name,
              ticketId: orderDetails?.ticket?.id,
              ticketName: orderDetails?.ticket?.name,
              ticketCount: orderDetails?.ticket?.qty || 1,
              value: orderDetails?.total_amount,
              currency: orderDetails?.ticket?.currency || "INR",
            });
            setLoading(false);
            router.push(`/tickets`);
          } else {
            trackPaymentFailed({
              event_id: orderDetails?.ticket?.event_id || id,
              event_name: name,
              value: orderDetails?.total_amount,
              currency: orderDetails?.ticket?.currency || "INR",
              failure_reason: rz?.message || "verification_failed",
            });
            message.error("payment failed");
          }
        },
      };

      const rzp1 = new Razorpay(options);
      rzp1.on("payment.failed", function (response) {
        trackPaymentFailed({
          event_id: orderDetails?.ticket?.event_id || id,
          event_name: name,
          value: orderDetails?.total_amount,
          currency: orderDetails?.ticket?.currency || "INR",
          failure_reason: response?.error?.description || "payment_declined",
        });
        message.error("payment failed");
      });
      rzp1.open();
    } catch (error) {
      setError(true);
    }
  };

  const createOrder = async (reg, customFields) => {
    buyerInfoRef.current = reg?.[0] || null;
    if (alreadyRegistered && is_unique_registration) {
      message.error(REGISTRATION_MESSAGES.ALREADY_REGISTERED_ACCOUNT);
      return;
    }

    if (is_unique_registration) {
      const duplicates = findDuplicatePhones(reg);
      if (duplicates.length > 0) {
        message.error(REGISTRATION_MESSAGES.DUPLICATE_IN_FORM);
        return;
      }
    }

    const fields = customFields || [];

    const payload = {
      currency: orderDetails.ticket.currency,
      event_id: orderDetails.ticket.event_id,
      source: "web",
      subtotal: orderDetails.subtotal,
      net_payable: orderDetails.total_amount,
      status: "draft",
      coupon_code: orderDetails.discount,
      session_id: orderDetails.session_id || null,
      seat_id:
        orderDetails?.ticket?.seat_id ||
        selectedSeats?.map((seat) => seat.seat_id) ||
        [],
      referral_code: getReferralCode(),
      registrations: reg?.map((data) => {
        const custom_form_response = fields
          .filter((f) => f.id !== 0 && f.id !== 1)
          .map((field) => {
            const fieldKey = field.name.replaceAll(" ", "_").toLowerCase();
            const fieldValue = data[fieldKey] ?? null;

            if (field.type === "checkbox") {
              return {
                custom_field_id: field.id,
                key: fieldValue,
                value: null,
              };
            }

            return {
              custom_field_id: field.id,
              key: fieldKey,
              value: fieldValue,
            };
          });

        const customFieldKeys = fields
          .filter((f) => f.id !== 0 && f.id !== 1)
          .map((field) => field.name.replaceAll(" ", "_").toLowerCase());

        const filteredData = Object.fromEntries(
          Object.entries(data).filter(
            ([key]) => !customFieldKeys.includes(key),
          ),
        );

        return {
          ...filteredData,

          country_code: (
            countryCodeToPhoneCode[data?.country_code] ||
            data?.country_code ||
            ""
          ).replace("+", ""),

          phone: data?.country_code
            ? (countryCodeToPhoneCode[data.country_code] || "").replace(
                "+",
                "",
              ) + data.phone
            : data.phone?.replace("+", ""),

          ticket_name: orderDetails?.ticket?.name,
          ticket_id: orderDetails?.ticket?.id,
          ticket_price: orderDetails?.ticket?.price,
          currency: orderDetails?.ticket?.currency,

          quantity: is_unique_registration
            ? 1
            : reg?.length > 1
              ? ""
              : orderDetails.ticket.qty,

          custom_form_response,
        };
      }),
    };
    trackAddAttendeeInfo({
      event_id: orderDetails?.ticket?.event_id || id,
      event_name: name,
      ticket_count: reg?.length || 1,
      value: orderDetails?.total_amount,
      currency: orderDetails?.ticket?.currency || "INR",
    });

    try {
      setLoading(true);
      const response = await fetch(
        process.env.NEXT_PUBLIC_API_URL + "/" + ENDPOINTS.ORDER,
        {
          method: "POST",
          credentials: "include",
          cache: "no-store",
          headers: {
            "Content-Type": "application/json",
            ...DEV_AUTH_HEADERS,
          },
          body: JSON.stringify(payload),
        },
      );
      const res = await response.json();
      if (res.status) {
        clearReferralCode();
        setCustomModal(false);
        if (is_required) {
          setApprovalModal(true);
        } else if (parseInt(orderDetails.total_amount) === 0) {
          trackPurchase({
            transaction_id: res.data?.id,
            event_id: orderDetails?.ticket?.event_id || id,
            event_name: name,
            ticket_id: orderDetails?.ticket?.id,
            ticket_name: orderDetails?.ticket?.name,
            price: 0,
            quantity: orderDetails?.ticket?.qty || 1,
            value: 0,
            currency: orderDetails?.ticket?.currency || "INR",
            event_city: address?.city || (venue || {})?.city,
            event_date: orderDetails?.date,
            coupon: orderDetails?.discount,
          });
          trackPixelPurchase({
            orderId: res.data?.id,
            eventId: orderDetails?.ticket?.event_id || id,
            eventName: orderDetails?.ticket?.name || name,
            ticketCount: orderDetails?.ticket?.qty || 1,
            value: 0,
            currency: orderDetails?.ticket?.currency || "INR",
          });
          router.push(`/tickets`);
        } else {
          const response1 = await fetch(
            process.env.NEXT_PUBLIC_API_URL + "/" + ENDPOINTS.PAYMENT,
            {
              method: "POST",
              credentials: "include",
              cache: "no-store",
              headers: {
                "Content-Type": "application/json",
                ...DEV_AUTH_HEADERS,
              },
              body: JSON.stringify({
                order_id: res.data?.id,
                payment_gateway: "razorpay",
              }),
            },
          );
          const res1 = await response1.json();
          if (res1.status) {
            paymentMethod(res1?.data, reg?.[0]);
          } else {
            setError(true);
            console.error("Payment init failed:", res1);
            message.error(res1.message);
          }
        }
      } else {
        setError(true);
        console.error("Order creation failed:", res);
        message.error(res.message || REGISTRATION_MESSAGES.ALREADY_REGISTERED);
      }
    } catch (error) {
      setError(true);
      console.error(error);
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const isStep3WithOutSeats =
    (activeStep === "zone" || activeStep === "tickets") &&
    effectiveHaveConfiguredSeats === false;
  return (
    <>
      <div className={styles.eventSummaryBooking} id="eventSummary">
        <Container>
          <Row
            gutter={[
              { xs: 8, sm: 8, md: 16, lg: 24 },
              { xs: 8, sm: 8, md: 16, lg: 24 },
            ]}
            align="bottom"
            className={styles.bookingMainRow}
          >
            <Col xs={24} sm={24} md={24} lg={12}>
              <Row
                gutter={[
                  { xs: 8, sm: 8, md: 24 },
                  { xs: 8, sm: 8, md: 24 },
                ]}
                className={styles.bookingInnerRow}
              >
                <Col xs={8}>
                  <div className={[styles.cover, styles.coverSm].join(" ")}>
                    <Link href={eventUrl(address?.city, slug)}>
                      <img
                        src={
                          cover_image?.[0]?.md ||
                          cover_image?.[0]?.sd ||
                          cover_image?.[0]?.url ||
                          placeholder.src
                        }
                        alt={name}
                      />
                    </Link>
                  </div>
                </Col>
                <Col xs={16}>
                  <Flex vertical gap={0} className={styles.eventSummaryMobile}>
                    <Link href={eventUrl(address?.city, slug)}>
                      <h1 className={[styles.title, styles.titleSm].join(" ")}>
                        {name}
                      </h1>
                    </Link>

                    {hasRegularTickets ? (
                      <>
                        <div className={styles.eventSubdetail}>
                          {yet_to_be_announced
                            ? `Venue to be announced${(venue || address)?.city ? ` : ${(venue || address).city}` : ""}`
                            : resolveVenueLabel(venue || address) ||
                              "Venue to be announced"}
                        </div>
                        <div className={styles.eventSubdetail}>
                          {formatEventDate(start_date)}{" "}
                          {formatEventTime(start_time)}
                          {(end_date || end_time) && (
                            <>
                              {" - "}
                              {end_date && end_date !== start_date
                                ? formatEventDate(end_date) + " "
                                : ""}
                              {formatEventTime(end_time)}
                            </>
                          )}
                        </div>
                        <EventTimezone timezone={timezone} UIfor={"mobile"} />
                      </>
                    ) : orderDetails ? (
                      <>
                        {orderDetails?.venue && (
                          <div className={styles.eventSubdetail}>
                            {orderDetails?.yet_to_be_announced
                              ? `Venue to be announced${orderDetails?.address?.city ? `, ${orderDetails.address.city}` : ""}`
                              : `${orderDetails?.venue}:${orderDetails?.address?.city}`}
                          </div>
                        )}

                        {orderDetails?.time &&
                        orderDetails?.date &&
                        orderDetails?.timezone ? (
                          <EventTimezone
                            timezone={orderDetails?.timezone}
                            UIfor={"mobile"}
                          />
                        ) : (
                          <></>
                        )}

                        {orderDetails?.time && orderDetails?.date && (
                          <div className={styles.eventSubdetail}>
                            {formatEventDate(orderDetails.date)}{" "}
                            {formatEventTime(orderDetails.time)}
                            {(orderDetails?.end_date ||
                              orderDetails?.end_time) && (
                              <>
                                {" - "}
                                {orderDetails?.end_date &&
                                orderDetails.end_date !== orderDetails.date
                                  ? formatEventDate(orderDetails.end_date) + " "
                                  : ""}
                                {formatEventTime(orderDetails?.end_time)}
                              </>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <></>
                    )}
                  </Flex>
                </Col>
              </Row>
            </Col>
            {orderDetails?.ticket && (
              <Col span={24} className="block! md:hidden!">
                <div className="my-2.5!">
                  <Ticket
                    name={orderDetails.ticket.name}
                    price={orderDetails.ticket.price}
                    currency={orderDetails.ticket.currency}
                    qty={orderDetails.ticket.qty}
                    description={readableTicketDescription(
                      orderDetails.ticket.description,
                    )}
                    status={selectedTicketMeta?.status}
                    available={selectedTicketMeta?.available}
                    available_quantity={selectedTicketMeta?.available_quantity}
                    is_sold_out={selectedTicketMeta?.is_sold_out}
                    min_per_transaction={
                      selectedTicketMeta?.min_per_transaction
                    }
                    order_id={enrollmentOrderId}
                    id={orderDetails?.ticket?.id}
                    index={orderDetails?.ticket?.index ?? selectedTicketIndex}
                  >
                    {orderDetails?.ticket?.is_season_pass &&
                      Array.isArray(orderDetails.ticket.covered_events) &&
                      orderDetails.ticket.covered_events.length > 0 && (
                        <SeasonPassCoveredEventsModal
                          className="-mt-1.5"
                          covered={orderDetails.ticket.covered_events}
                          highlightChildEventId={orderDetails?.event_id}
                          highlightDate={orderDetails?.date}
                          highlightTime={orderDetails?.time}
                        />
                      )}
                  </Ticket>
                </div>
              </Col>
            )}

            <Col xs={24} sm={24} md={24} lg={12}>
              <ComponentToRender
                is_zone={is_zone}
                is_sitting={is_sitting}
                haveConfiguredSeats={effectiveHaveConfiguredSeats}
                onNoZones={handleNoZones}
                alreadyRegistered={alreadyRegistered}
                enrollmentOrderId={enrollmentOrderId}
                render={"tab"}
                data={data}
                activeStep={activeStep}
                setActiveStep={setActiveStep}
                isGroup={isGroup}
                needsVenueSelection={needsVenueSelection}
                orderDetails={orderDetails}
                setOrderDetails={setOrderDetails}
                setCustomModal={setCustomModal}
                selectedSeats={selectedSeats}
                setSelectedSeats={setSelectedSeats}
                createOrder={createOrder}
                is_required={is_required}
                approvalModal={approvalModal}
                setApprovalModal={setApprovalModal}
                loading={loading}
                setLoading={setLoading}
                dates={selectedDates}
                setDates={setSelectedDates}
                tickets={selectedTickets}
                setTickets={setSelectedTickets}
              />
            </Col>
          </Row>
        </Container>
      </div>

      {/* <Container style={{ marginTop: 24 }}> */}
      <div
        style={{
          marginTop: activeStep === "seating" || activeStep === "zone" ? 0 : 24,
        }}
        className={
          activeStep === "seating" || activeStep === "zone" ? "" : "container"
        }
      >
        <Row
          gutter={
            activeStep === "seating" || activeStep === "zone"
              ? [0, 0]
              : [24, 24]
          }
          className={styles.bookingContentRow}
        >
          {activeStep !== "seating" && activeStep !== "zone" && (
            <Col xs={0} sm={0} md={0} lg={12} xl={10}>
              <div className={styles.bookingSidebarSticky}>
                {hasRegularTickets ? (
                  <>
                    <h4 className={styles.sectionTitle2}>Details</h4>
                    <div className={styles.eventSummary}>
                      <div className={styles.eventSummaryItem}>
                        <Avatar
                          size={45}
                          icon={<IconCalendarEvent size={20} />}
                        />
                        <span>
                          <strong>Starts at: </strong>
                          <ConvertedTime
                            date={start_date}
                            time={start_time}
                            timezone={timezone}
                          />
                        </span>
                      </div>

                      <div className={styles.eventSummaryItem}>
                        <Avatar
                          size={45}
                          icon={<IconCalendarEvent size={20} />}
                        />
                        <span>
                          <strong>Ends at: </strong>
                          <ConvertedTime
                            date={end_date}
                            time={end_time}
                            timezone={timezone}
                          />
                        </span>
                      </div>
                      {/* <div className={styles.eventSummaryItem}> */}
                      <EventTimezone timezone={timezone} />
                      {/* </div> */}

                      <div className="block">
                        <div className={styles.eventSummaryItem}>
                          <Avatar size={45} icon={<IconMapPin size={20} />} />
                          <span>
                            {yet_to_be_announced
                              ? `Venue to be announced${(venue || address)?.city ? `, ${(venue || address).city}` : ""}`
                              : resolveVenueLabel(venue || address) ||
                                "Venue to be announced"}
                          </span>
                          <LocationButton
                            place_id={(venue || address)?.google_place_id}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {orderDetails ? (
                      <>
                        <h4 className={styles.sectionTitle2}>Details</h4>
                        <div className={styles.eventSummary}>
                          {orderDetails?.time && orderDetails?.date && (
                            <div className={styles.eventSummaryItem}>
                              <Avatar
                                size={45}
                                icon={<IconCalendarEvent size={20} />}
                              />
                              <span>
                                <strong>Starts at: </strong>
                                <ConvertedTime
                                  date={orderDetails?.date}
                                  time={orderDetails?.time}
                                  timezone={orderDetails?.timezone}
                                />
                              </span>
                            </div>
                          )}
                          {orderDetails?.end_time && orderDetails?.end_date && (
                            <div className={styles.eventSummaryItem}>
                              <Avatar
                                size={45}
                                icon={<IconCalendarEvent size={20} />}
                              />
                              <span>
                                <strong>Ends at: </strong>
                                <ConvertedTime
                                  date={orderDetails?.end_date}
                                  time={orderDetails?.end_time}
                                  timezone={orderDetails?.timezone}
                                />
                              </span>
                            </div>
                          )}
                          {orderDetails?.time &&
                          orderDetails?.date &&
                          orderDetails?.timezone ? (
                            <EventTimezone timezone={orderDetails?.timezone} />
                          ) : (
                            <></>
                          )}

                          {orderDetails?.venue && (
                            <div className={styles.eventSummaryItem}>
                              <Avatar
                                size={45}
                                icon={<IconMapPin size={20} />}
                              />
                              <span>
                                {orderDetails?.yet_to_be_announced
                                  ? `Venue to be announced${orderDetails?.address?.city ? `, ${orderDetails.address.city}` : ""}`
                                  : `${orderDetails?.venue}: ${orderDetails?.address?.city}`}
                              </span>
                              {!orderDetails?.yet_to_be_announced && (
                                <LocationButton
                                  place_id={
                                    orderDetails?.address?.google_place_id
                                  }
                                />
                              )}
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <></>
                    )}
                  </>
                )}
                {orderDetails?.ticket && (
                  <>
                    <Ticket
                      name={orderDetails?.ticket?.name}
                      index={orderDetails?.ticket?.index ?? selectedTicketIndex}
                      price={(() => {
                        // For seating / zone steps the ticket.price in
                        // orderDetails may have been accumulated as a total.
                        // Always display the per-seat unit price:
                        //   1. Use the real ticket metadata price if available.
                        //   2. Otherwise derive it from subtotal ÷ qty.
                        //   3. Fall back to the raw stored price.
                        const isSeatingStep =
                          activeStep === "seating" || activeStep === "zone";
                        if (isSeatingStep) {
                          const qty = orderDetails?.ticket?.qty;
                          if (selectedTicketMeta?.price != null) {
                            return selectedTicketMeta.price;
                          }
                          if (qty > 0 && orderDetails?.subtotal != null) {
                            return orderDetails.subtotal / qty;
                          }
                        }
                        return orderDetails?.ticket?.price;
                      })()}
                      currency={orderDetails?.ticket?.currency}
                      qty={orderDetails?.ticket?.qty}
                      description={readableTicketDescription(
                        orderDetails?.ticket?.description,
                      )}
                      status={selectedTicketMeta?.status}
                      available={selectedTicketMeta?.available}
                      available_quantity={
                        selectedTicketMeta?.available_quantity
                      }
                      is_sold_out={selectedTicketMeta?.is_sold_out}
                      min_per_transaction={
                        selectedTicketMeta?.min_per_transaction
                      }
                      orderDetails={orderDetails}
                      order_id={enrollmentOrderId}
                      id={orderDetails?.ticket?.id}
                    >
                      {orderDetails?.ticket?.is_season_pass &&
                        Array.isArray(orderDetails.ticket.covered_events) &&
                        orderDetails.ticket.covered_events.length > 0 && (
                          <SeasonPassCoveredEventsModal
                            className="-mt-1.5"
                            covered={orderDetails.ticket.covered_events}
                            highlightChildEventId={orderDetails?.event_id}
                            highlightDate={orderDetails?.date}
                            highlightTime={orderDetails?.time}
                          />
                        )}
                    </Ticket>
                  </>
                )}
              </div>
            </Col>
          )}

          <Col
            xs={24}
            sm={24}
            md={{ span: 20, offset: 2 }}
            // lg={{ span: activeStep !== "3" ? 12 : 24, offset: 0 }}
            // xl={{ span: activeStep !== "3" ? 12 : 24, offset: 2 }}
            lg={{
              span:
                isStep3WithOutSeats ||
                (activeStep !== "seating" && activeStep !== "zone")
                  ? 12
                  : 24,
              offset: 0,
            }}
            xl={{
              span:
                isStep3WithOutSeats ||
                (activeStep !== "seating" && activeStep !== "zone")
                  ? 12
                  : 24,
              offset: 2,
            }}
            style={{
              marginInlineStart:
                activeStep === "seating" || activeStep === "zone"
                  ? "0px"
                  : "auto",
            }}
          >
            <ComponentToRender
              is_sitting={is_sitting}
              is_zone={is_zone}
              haveConfiguredSeats={effectiveHaveConfiguredSeats}
              onNoZones={handleNoZones}
              alreadyRegistered={alreadyRegistered}
              enrollmentOrderId={enrollmentOrderId}
              selectedSeats={selectedSeats}
              setSelectedSeats={setSelectedSeats}
              render={"component"}
              data={data}
              isTicketTransferable={is_ticket_transferable}
              activeStep={activeStep}
              setActiveStep={setActiveStep}
              isGroup={isGroup}
              needsVenueSelection={needsVenueSelection}
              orderDetails={orderDetails}
              setOrderDetails={setOrderDetails}
              setCustomModal={setCustomModal}
              createOrder={createOrder}
              is_required={is_required}
              approvalModal={approvalModal}
              setApprovalModal={setApprovalModal}
              loading={loading}
              setLoading={setLoading}
              dates={selectedDates}
              setDates={setSelectedDates}
              tickets={selectedTickets}
              setTickets={setSelectedTickets}
            />
          </Col>
        </Row>
      </div>
      <div className={styles.bookingFooter} ref={footerRef}>
        <Container>
          <Row gutter={[24, 24]}>
            <Col
              xs={24}
              sm={24}
              // md={{ span: 20, offset: 2 }}
              // lg={{ span: 12, offset: 12 }}
              lg={{
                span:
                  isStep3WithOutSeats ||
                  (activeStep !== "seating" && activeStep !== "zone")
                    ? 12
                    : 24,
                offset: 0,
              }}
              xl={{
                span:
                  isStep3WithOutSeats ||
                  (activeStep !== "seating" && activeStep !== "zone")
                    ? 12
                    : 24,
                offset: 2,
              }}
              style={{
                marginInlineStart:
                  activeStep === "seating" || activeStep === "zone"
                    ? "0px"
                    : "auto",
              }}
            >
              <ComponentToRender
                is_zone={is_zone}
                is_sitting={is_sitting}
                haveConfiguredSeats={effectiveHaveConfiguredSeats}
                onNoZones={handleNoZones}
                alreadyRegistered={alreadyRegistered}
                enrollmentOrderId={enrollmentOrderId}
                render={"buttons"}
                data={data}
                activeStep={activeStep}
                setActiveStep={setActiveStep}
                isGroup={isGroup}
                needsVenueSelection={needsVenueSelection}
                orderDetails={orderDetails}
                setOrderDetails={setOrderDetails}
                setCustomModal={setCustomModal}
                selectedSeats={selectedSeats}
                setSelectedSeats={setSelectedSeats}
                createOrder={createOrder}
                loading={loading}
                setLoading={setLoading}
                approvalModal={approvalModal}
                is_required={is_required}
                customize={customize}
                dates={selectedDates}
                setDates={setSelectedDates}
                tickets={selectedTickets}
                setTickets={setSelectedTickets}
              />
            </Col>
          </Row>
        </Container>
      </div>

      <Modal
        open={customModal}
        closable={false}
        closeIcon={false}
        title={"Fill out this information to book your ticket"}
        footer={<></>}
      >
        <Form.Provider
          onFormFinish={(name, info) => {
            const cleanValues = (values) => {
              if (!values || typeof values !== "object") return values;
              const cleaned = {};
              for (const [k, v] of Object.entries(values)) {
                if (Array.isArray(v)) {
                  cleaned[k] = v.map((item) =>
                    typeof item === "string"
                      ? item.replace(/___\d+$/, "")
                      : item,
                  );
                } else if (typeof v === "string") {
                  cleaned[k] = v.replace(/___\d+$/, "");
                } else {
                  cleaned[k] = v;
                }
              }
              return cleaned;
            };

            setRegistrations((prev) => {
              const updated = { ...prev, [name]: cleanValues(info.values) };
              const currentValues = Object.values(updated);

              if (is_unique_registration) {
                const duplicates = findDuplicatePhones(currentValues);
                if (duplicates.some((item) => item.index === parseInt(name))) {
                  message.error(REGISTRATION_MESSAGES.DUPLICATE_IN_FORM);
                  return prev;
                }
              }

              const targetQty = show_single_registration
                ? 1
                : orderDetails?.ticket?.qty || 1;

              if (parseInt(name) === targetQty - 1) {
                createOrder(
                  currentValues.map((values) => ({ ...values })),
                  ticket_custom_field,
                );
              }

              return updated;
            });
          }}
        >
          <CreateCustomForm
            is_unique_registration={is_unique_registration}
            ticket_custom_field={
              !is_custom_form
                ? [
                    {
                      id: 0,
                      label: null,
                      name: "First name",
                      type: "short_answer",
                      options: null,
                      is_required: true,
                    },
                    {
                      id: 1,
                      label: null,
                      name: "Last name",
                      type: "short_answer",
                      options: null,
                      is_required: true,
                    },
                  ]
                : [
                    {
                      id: 0,
                      label: null,
                      name: "First name",
                      type: "short_answer",
                      options: null,
                      is_required: true,
                    },
                    {
                      id: 1,
                      label: null,
                      name: "Last name",
                      type: "short_answer",
                      options: null,
                      is_required: true,
                    },
                    ...ticket_custom_field,
                  ]
            }
            show_single_registration={show_single_registration}
            setCustomModal={setCustomModal}
            is_custom_form={is_custom_form}
            qty={orderDetails?.ticket?.qty}
            loading={loading}
          />
        </Form.Provider>
      </Modal>
    </>
  );
};

export default Booking;
